import { createSlice } from '@reduxjs/toolkit';

// Load favoriteIds from localStorage if available
const loadFavoriteIds = () => {
  try {
    const savedIds = localStorage.getItem('favoriteCarIds');
    if (savedIds) {
      return new Set(JSON.parse(savedIds));
    }
  } catch (error) {
    console.error('Error loading favorite IDs from localStorage:', error);
  }
  return new Set();
};

// Save favoriteIds to localStorage
const saveFavoriteIds = (ids) => {
  try {
    const idsArray = Array.from(ids);
    localStorage.setItem('favoriteCarIds', JSON.stringify(idsArray));
  } catch (error) {
    console.error('Error saving favorite IDs to localStorage:', error);
  }
};

// Function to create a set from an array or existing set
const createNewSet = (initialData = []) => {
  return new Set(initialData);
};

const filter = createSlice({
  name: 'car',
  initialState: {
    detail: null,
    favoriteIds: loadFavoriteIds() // Initialize from localStorage
  },
  reducers: {
    setCarDetail: (state, action) => {
      // If the car is in our favorites, make sure it's marked as favorite
      if (action.payload && action.payload._id) {
        // Check if this car ID is in our favoriteIds set
        const favoriteIdsArray = Array.from(state.favoriteIds || []);
        const wasFavorite = favoriteIdsArray.includes(action.payload._id);
        
        // Priority order for isFavorite:
        // 1. Use the explicitly provided value
        // 2. Use our favoriteIds set if we know it's favorited
        // 3. Default to false
        const isFavorite = action.payload.isFavorite !== undefined 
          ? action.payload.isFavorite 
          : wasFavorite;
        
        // Update favorite status before setting in state
        state.detail = {
          ...action.payload,
          isFavorite
        };
        
        // Update our tracking set if favorited
        if (isFavorite) {
          if (!state.favoriteIds) {
            state.favoriteIds = createNewSet();
          }
          state.favoriteIds.add(action.payload._id);
          // Persist to localStorage
          saveFavoriteIds(state.favoriteIds);
        }
      } else {
        state.detail = action.payload;
      }
    },
    updateCarDetail: (state, action) => {
      // For favorite status updates, track the ID
      if (action.payload && action.payload._id && action.payload.isFavorite !== undefined) {
        // Make sure we have a Set to work with
        if (!state.favoriteIds) {
          state.favoriteIds = createNewSet();
        }
        
        if (action.payload.isFavorite) {
          state.favoriteIds.add(action.payload._id);
        } else {
          // Delete from our tracking set if unfavorited
          state.favoriteIds.delete(action.payload._id);
        }
        
        // Persist updated favorite IDs to localStorage
        saveFavoriteIds(state.favoriteIds);
      }
      
      // If detail is already loaded, update it with the new data
      if (state.detail) {
        state.detail = {
          ...state.detail,
          ...action.payload
        };
      }
    },
    // Add a new action to check favorite status for a given car ID
    checkFavoriteStatus: (state, action) => {
      if (action.payload && action.payload._id) {
        const isFavorite = state.favoriteIds && state.favoriteIds.has(action.payload._id);
        // Update the detail object if it exists and matches the ID
        if (state.detail && state.detail._id === action.payload._id) {
          state.detail.isFavorite = isFavorite;
        }
        // Return value is not used by reducer but can be useful for debugging
        return isFavorite;
      }
      return false;
    }
  },
});

export const { setCarDetail, updateCarDetail, checkFavoriteStatus } = filter.actions;
export default filter.reducer;
